import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Tabs, Alert, Segmented } from 'antd';
import { ROLES } from '@/constants';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import { useAuthStore } from '@/store/useAuthStore';
import AuditLogsTab from './AuditLogsTab';
import HistorySummary from './HistorySummary';
import SnapshotsTab from './SnapshotsTab';
import StatisticsTab from './StatisticsTab';
import TimelineTab from './TimelineTab';

/**
 * ResourceHistory Component
 *
 * Universal history viewer for any HLB model (User, Employee, Company, etc.).
 * Displays:
 * - Timeline: Combined view of snapshots + audit logs
 * - Snapshots: Full state captures at specific points in time
 * - Audit Logs: Field-level change tracking
 * - Statistics: Summary of document history
 *
 * Usage:
 * <ResourceHistory modelName="User" documentId="60d21b4667d0d8992e610c85" />
 *
 * @param {string} modelName - The model name (e.g., "User", "Employee")
 * @param {string} documentId - The document ID
 * @param {'profile'} [variant] - profile page: key numbers on top, the
 *   lists as pills, the timeline as cards
 * @param {string} [createdAt] - profile: when the record was created
 */
export default function ResourceHistory({
  modelName,
  documentId,
  variant,
  createdAt,
}) {
  const isProfile = variant === 'profile';
  const { t } = useTranslation(['history', 'forms', 'messages']);
  const notification = useNotify();
  const { user } = useAuthStore();

  const canViewSnapshots =
    user?.roles?.some(
      (role) => role === ROLES.superAdmin || role === ROLES.techAdmin
    ) || false;

  const [activeTab, setActiveTab] = useState('timeline');

  // Timeline query
  const {
    data: timelineData,
    isLoading: timelineLoading,
    error: timelineError,
  } = useQuery({
    queryKey: [QUERY_KEY.history, modelName, documentId, 'timeline'],
    queryFn: async () => {
      const response = await API.history.getTimeline(modelName, documentId);
      return response.data || [];
    },
    enabled: !!modelName && !!documentId && activeTab === 'timeline',
    retry: false,
    onError: (err) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: err.response?.data?.message || t('error_loading_history'),
      });
    },
  });

  // Snapshots query
  const {
    data: snapshotsData,
    isLoading: snapshotsLoading,
    error: snapshotsError,
  } = useQuery({
    queryKey: [QUERY_KEY.history, modelName, documentId, 'snapshots'],
    queryFn: async () => {
      const response = await API.history.getSnapshots(modelName, documentId);
      return response.data || [];
    },
    enabled: !!modelName && !!documentId && activeTab === 'snapshots',
    retry: false,
    onError: (err) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: err.response?.data?.message || t('error_loading_history'),
      });
    },
  });

  // Audit logs query
  const {
    data: auditLogsData,
    isLoading: auditLogsLoading,
    error: auditLogsError,
  } = useQuery({
    queryKey: [QUERY_KEY.history, modelName, documentId, 'auditLogs'],
    queryFn: async () => {
      const response = await API.history.getAuditLogs(modelName, documentId);
      return response.data || [];
    },
    enabled: !!modelName && !!documentId && activeTab === 'auditLogs',
    retry: false,
    onError: (err) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: err.response?.data?.message || t('error_loading_history'),
      });
    },
  });

  // Statistics query
  const {
    data: statisticsData,
    isLoading: statisticsLoading,
    error: statisticsError,
  } = useQuery({
    queryKey: [QUERY_KEY.history, modelName, documentId, 'statistics'],
    queryFn: async () => {
      const response = await API.history.getStatistics(modelName, documentId);
      return response.data || null;
    },
    // The profile shows the numbers above every list.
    enabled:
      !!modelName && !!documentId && (isProfile || activeTab === 'statistics'),
    retry: false,
    onError: (err) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: err.response?.data?.message || t('error_loading_history'),
      });
    },
  });

  // Determine current error state
  const error =
    timelineError || snapshotsError || auditLogsError || statisticsError;

  const allTabs = [
    {
      key: 'timeline',
      label: t('ui.timeline', { ns: 'history' }),
      children: (
        <TimelineTab
          data={timelineData || []}
          loading={timelineLoading}
          modelName={modelName}
          canViewSnapshots={canViewSnapshots}
          variant={variant}
        />
      ),
    },
    {
      key: 'snapshots',
      label: t('ui.snapshots', { ns: 'history' }),
      children: (
        <SnapshotsTab
          data={snapshotsData || []}
          loading={snapshotsLoading}
          modelName={modelName}
        />
      ),
    },
    {
      key: 'auditLogs',
      label: t('ui.auditLogs', { ns: 'history' }),
      children: (
        <AuditLogsTab
          data={auditLogsData || []}
          loading={auditLogsLoading}
          modelName={modelName}
          variant={variant}
        />
      ),
    },
    {
      key: 'statistics',
      label: t('ui.statistics', { ns: 'history' }),
      children: (
        <StatisticsTab
          data={statisticsData || null}
          loading={statisticsLoading}
          modelName={modelName}
          canViewSnapshots={canViewSnapshots}
        />
      ),
    },
  ];

  const tabs = allTabs.filter(
    (tab) =>
      (tab.key !== 'snapshots' || canViewSnapshots) &&
      // On the profile the statistics sit above the lists.
      (tab.key !== 'statistics' || !isProfile)
  );

  if (error) {
    return (
      <div className="p-4">
        <Alert
          message={t('error', { ns: 'messages' })}
          description={error?.message || t('error_loading_history')}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (isProfile) {
    const current = tabs.find((tab) => tab.key === activeTab) || tabs[0];
    return (
      <div className="resource-history grid gap-4 pt-3">
        <HistorySummary
          data={statisticsData || null}
          canViewSnapshots={canViewSnapshots}
          createdAt={createdAt}
        />
        <Segmented
          value={current.key}
          onChange={setActiveTab}
          options={tabs.map((tab) => ({ value: tab.key, label: tab.label }))}
          className="w-max"
        />
        <div>{current.children}</div>
      </div>
    );
  }

  return (
    <div className="resource-history h-full px-4 pt-4">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabs}
        className="h-full"
      />
    </div>
  );
}
